import React from 'react';
import Popover from '@mui/material/Popover';
import { scoreWord } from './scoreWords.js';
import Tooltip from '@mui/material/Tooltip';
import StarOutlineRounded from '@mui/icons-material/StarOutlineRounded';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutlineOutlined';
import VpnKeyOutlined from '@mui/icons-material/VpnKeyOutlined';
import SchoolOutlined from '@mui/icons-material/SchoolOutlined';
import CheckRounded from '@mui/icons-material/CheckRounded';
import { HomeTypeSvgIcon } from './homeTypeIcons.js';
import { colourMetricDescription, colourMetricLabel } from './colourMetrics.js';
import { COLOUR_SCHEME_STEPS } from './areaData.js';
import './colourMetricMenu.css';

// Kartvy's "Färgkarta" menu (its own button on the toolbar): what the areas are coloured by. One metric
// at a time; picking one keeps the menu open so the metrics can be compared, and a click outside or
// Escape closes it. Each option explains itself in a tooltip as soon as it's pointed at (a tap shows it on
// phones); the labels and explanations are in colourMetrics.js, shared with the legend.
export // Home-type icons from homeTypeIcons.js, as in the filter.
const ApartmentIcon = (props) => <HomeTypeSvgIcon type="apartment" {...props} />;
const HouseIcon = (props) => <HomeTypeSvgIcon type="house" {...props} />;

export const COLOUR_METRICS = [
    { key: 'totalScore', Icon: StarOutlineRounded },
    { key: 'squareMeterPriceApartment', Icon: ApartmentIcon },
    { key: 'squareMeterPriceHouse', Icon: HouseIcon },
    { key: 'income', Icon: AccountBalanceWalletOutlined },
    { key: 'unemployment', Icon: WorkOutlineOutlined },
    { key: 'ownedVsRented', Icon: VpnKeyOutlined },
    { key: 'education', Icon: SchoolOutlined },
];

// placement 'rail': beside kartvy's toolbar rail on desktop, above its bottom bar on phones.
// placement 'below': under a round map button (listvy), right edges aligned.
const PLACEMENTS = {
    rail: { anchorOrigin: { vertical: 'top', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'left' } },
    railPhone: { anchorOrigin: { vertical: 'top', horizontal: 'center' }, transformOrigin: { vertical: 'bottom', horizontal: 'center' } },
    below: { anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' } },
};

const SCALE_STEPS = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];
// The colour scale shown under the metric list — just the regular colours now (the heat-map option was
// removed, so there's nothing to choose between).
const ColourMetricMenu = ({ anchorEl, onClose, checkedState, onSelect, swedish, placement = 'rail' }) => {
    const open = Boolean(anchorEl);
    const isPhone = typeof window !== 'undefined' && window.matchMedia?.('(max-width: 700px)').matches;
    const place = placement === 'rail' && isPhone ? 'railPhone' : placement;
    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={PLACEMENTS[place].anchorOrigin}
            transformOrigin={PLACEMENTS[place].transformOrigin}
            marginThreshold={12}
            slotProps={{ paper: { className: `colour-metric-menu is-${place}` } }}
        >
            <div className="colour-metric-head">
                <span className="colour-metric-title" id="colour-metric-title">
                    {swedish ? 'Färga kartan efter' : 'Colour the map by'}
                </span>
            </div>
            <div className="colour-metric-list" role="menu" aria-labelledby="colour-metric-title">
                {COLOUR_METRICS.map(({ key, Icon }) => {
                    const active = Boolean(checkedState?.[key]);
                    // Betyg weighs the others together, so it sits apart: on top, the single measures below a label.
                    const groupLabel = key === 'squareMeterPriceApartment' && (
                        <div className="colour-metric-group" role="separator" aria-label={swedish ? 'Enskilda mått' : 'Single measures'}>
                            <span className="colour-metric-group-label" aria-hidden="true">{swedish ? 'Enskilda mått' : 'Single measures'}</span>
                            <span className="colour-metric-group-line" aria-hidden="true" />
                        </div>
                    );
                    return (
                        <React.Fragment key={key}>
                        {groupLabel}
                        <Tooltip
                            title={colourMetricDescription(key, swedish)}
                            placement={isPhone ? 'top' : 'right'}
                            arrow
                            disableInteractive
                            enterDelay={0}
                            enterNextDelay={0}
                            enterTouchDelay={0}
                            leaveTouchDelay={2500}
                            slotProps={{ tooltip: { className: 'colour-metric-tooltip' }, arrow: { className: 'colour-metric-tooltip-arrow' } }}
                        >
                            <button
                                type="button"
                                role="menuitemradio"
                                aria-checked={active}
                                className={`colour-metric-item${active ? ' is-active' : ''}${key === 'totalScore' ? ' is-total' : ''}`}
                                onClick={() => onSelect(key)}
                            >
                                <Icon className="colour-metric-icon" aria-hidden="true" />
                                <span className="colour-metric-label">{colourMetricLabel(key, swedish)}</span>
                                {active && <CheckRounded className="colour-metric-check" aria-hidden="true" />}
                            </button>
                        </Tooltip>
                        </React.Fragment>
                    );
                })}
            </div>
            <div className="colour-metric-scale">
                <span className="colour-metric-scheme-bar" aria-hidden="true">
                    {SCALE_STEPS.map((step) => (
                        <i key={step} style={{ background: COLOUR_SCHEME_STEPS.discrete[step] }} />
                    ))}
                </span>
                <span className="colour-metric-ends" aria-hidden="true">
                    <span>{scoreWord(1, swedish)}</span>
                    <span>{scoreWord(6, swedish)}</span>
                </span>
            </div>
        </Popover>
    );
};

export default ColourMetricMenu;
